/**
 * FinanceCommandCenter — Phase 1 of the /accounting redesign.
 *
 * CFO-grade overview surface placed above the operational ledger.
 * Composes: time-anchored header → 4 KPI cards (Cash hero) → Liquidity
 * Forecast (with min-cash threshold + machine read) → Decision Queue →
 * AR Aging + Upcoming AP → Market Contribution.
 *
 * Wiring scope (Phase 1):
 *  - Cash Position, Net Margin, AR Aging, Decision Queue, Market Contribution
 *    derive from existing AppData (jobs/financials/payRun/holdbacks/disputes).
 *  - DSO, Working Capital Ratio, Liquidity Forecast, Upcoming AP are
 *    LABELLED STUBS with sample shape — flagged with a "Beta · estimate"
 *    pill so we don't fake numbers we haven't wired yet.
 *
 * Phase 2 (separate milestone): commentary lines under each KPI,
 * WIP / Unbilled card, Cash Conversion Cycle, "This Week's Headline".
 */
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Info, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Tooltip as RTooltip,
  LineChart, Line,
} from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import type { Job } from "@/data/types";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import type { PayRunRow } from "@/data/finance/payRun/usePayRunViews";
import { computeDso, computeArAging, computeMarginDelta, computeCollectedInWindow, computeTopReceivables, type TopReceivable } from "@/data/finance/receivablesAnalytics";
import { computeCashPosition, computeLiquidityForecast, type LiquidityPoint } from "@/data/finance/cashflowAnalytics";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;

// ───────────────────────────────────────────────────────────────────────
// KPI Card
// ───────────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: { value: string; direction: "up" | "down"; tone: "good" | "bad" | "muted" };
  hint?: string;
  info?: string;
  beta?: boolean;
  sparkline?: number[];
  hero?: boolean;
}

function KpiCard({ label, value, unit, delta, hint, info, beta, sparkline, hero }: KpiCardProps) {
  const deltaColor =
    delta?.tone === "good" ? "text-success"
    : delta?.tone === "bad" ? "text-destructive"
    : "text-muted-foreground";

  return (
    <div className={`bg-background px-5 py-5 flex flex-col ${hero ? "md:col-span-2" : ""}`}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[12px] uppercase tracking-[0.16em] text-muted-foreground font-medium">{label}</p>
        {info && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">{info}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {beta && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-warning/40 text-warning font-medium">
            Beta · est.
          </Badge>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`font-semibold tabular-nums tracking-tight text-foreground ${hero ? "text-[40px] leading-none" : "text-[28px] leading-none"}`}>{value}</span>
        {unit && <span className="text-base text-muted-foreground">{unit}</span>}
      </div>
      {delta && (
        <div className={`flex items-center gap-1.5 text-[13px] mt-2 ${deltaColor}`}>
          {delta.direction === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
          <span className="font-medium tabular-nums">{delta.value}</span>
          {hint && <span className="text-muted-foreground">{hint}</span>}
        </div>
      )}
      {sparkline && sparkline.length > 1 && (
        <div className="mt-2 -mx-1 h-9">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline.map((v, i) => ({ i, v }))}>
              <Line type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Decision Queue Item
// ───────────────────────────────────────────────────────────────────────

type QueueSeverity = "critical" | "warn" | "info";

interface QueueItem {
  id: string;
  severity: QueueSeverity;
  title: string;
  context: string;
  tag: string;
  href: string;
  action: string;
}

function QueueRow({ item }: { item: QueueItem }) {
  const stripe =
    item.severity === "critical" ? "bg-destructive"
    : item.severity === "warn" ? "bg-warning"
    : "bg-primary";
  const tagTone =
    item.severity === "critical" ? "bg-destructive/10 text-destructive"
    : item.severity === "warn" ? "bg-warning/10 text-warning"
    : "bg-primary/10 text-primary";
  return (
    <div className="relative pl-3.5 pr-3 py-2.5 bg-background hover:bg-muted/30 transition-colors">
      <div className={`absolute left-0 top-2.5 bottom-2.5 w-0.5 ${stripe}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-foreground truncate">{item.title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.context}</p>
          <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 ${tagTone} font-medium`}>{item.tag}</span>
        </div>
        <Link to={item.href} className="text-[11px] text-primary hover:underline whitespace-nowrap mt-0.5">
          {item.action} →
        </Link>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Liquidity Forecast — sample shape, threshold line, machine read.
// ───────────────────────────────────────────────────────────────────────

function LiquidityForecast({ points, projectedMin }: { points: LiquidityPoint[]; projectedMin: number }) {
  const data = points.map((p) => ({
    month: p.month,
    actual: p.actual,
    projected: p.projected,
  }));

  // Min-cash threshold scales with the data — 40% of trailing peak, floored at $1M.
  const peak = Math.max(...data.map((d) => Math.max(d.actual ?? 0, d.projected ?? 0)));
  const minCash = Math.max(Math.round(peak * 0.4), 1_000_000);
  const safe = projectedMin > minCash;

  return (
    <div className="bg-background border border-border/60 p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Liquidity Forecast</p>
          <p className="text-[12px] text-foreground/80 mt-1">Next {data.filter((d) => d.projected != null).length - 1} months · projected cash position</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-primary inline-block" />Actual</span>
          <span className="flex items-center gap-1.5"><span className="w-3 border-t border-dashed border-primary inline-block" />Projected</span>
        </div>
      </div>
      <div className="flex-1 min-h-[160px] mt-2 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => fmtCompact(v)} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={42} />
            <RTooltip
              cursor={{ stroke: "hsl(var(--border))" }}
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 11, borderRadius: 6 }}
              formatter={(v: number) => fmtCompact(v)}
            />
            <ReferenceLine y={minCash} stroke="hsl(var(--destructive))" strokeDasharray="3 3" strokeWidth={1} label={{ value: `Min ${fmtCompact(minCash)}`, position: "insideTopRight", fontSize: 9, fill: "hsl(var(--destructive))" }} />
            <Area type="monotone" dataKey="actual" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#cashFill)" />
            <Line type="monotone" dataKey="projected" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="4 4" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={`mt-2 px-2.5 py-1.5 text-[11px] flex items-center gap-2 ${safe ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
        {safe ? <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> : <Info className="h-3 w-3 flex-shrink-0" />}
        <span className="italic truncate">
          {safe
            ? `Projected to stay above ${fmtCompact(projectedMin)} — no action required.`
            : `Projected to dip to ${fmtCompact(projectedMin)} (below ${fmtCompact(minCash)}) — review collections.`}
        </span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// AR Aging horizontal bars
// ───────────────────────────────────────────────────────────────────────

interface ArBucket { label: string; amount: number; tone: "good" | "warn" | "bad" }

function ArAging({ buckets }: { buckets: ArBucket[] }) {
  const total = buckets.reduce((s, b) => s + b.amount, 0);
  return (
    <div className="bg-background border border-border/60 p-4">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">AR Aging</p>
      <div className="space-y-2">
        {buckets.map((b) => {
          const pct = total > 0 ? (b.amount / total) * 100 : 0;
          const barColor = b.tone === "bad" ? "bg-destructive/70" : b.tone === "warn" ? "bg-warning/70" : "bg-success/70";
          return (
            <div key={b.label} className="grid grid-cols-[80px_1fr_80px_40px] items-center gap-3 text-[13px]">
              <span className="text-muted-foreground">{b.label}</span>
              <div className="h-1 bg-muted overflow-hidden">
                <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-right tabular-nums font-medium">{fmtCompact(b.amount)}</span>
              <span className="text-right tabular-nums text-muted-foreground">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border/60 mt-3 pt-2 flex items-center justify-between text-[13px]">
        <span className="font-medium">Total AR</span>
        <span className="font-semibold tabular-nums">{fmtCompact(total)}</span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Top Receivables — real, computed from open invoices
// ───────────────────────────────────────────────────────────────────────

function TopReceivables({ rows }: { rows: TopReceivable[] }) {
  const total = rows.reduce((s, r) => s + r.openBalance, 0);
  return (
    <div className="bg-background border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Top Receivables</p>
        <span className="text-[11px] text-muted-foreground">Top {rows.length} by open balance</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-[12px] text-muted-foreground py-2">No open receivables.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const tone =
              r.oldestDueDays > 60 ? "text-destructive"
              : r.oldestDueDays > 30 ? "text-warning"
              : "text-muted-foreground";
            const ageLabel =
              r.oldestDueDays === 0 ? "current"
              : `${r.oldestDueDays}d overdue`;
            return (
              <div key={r.customerName} className="grid grid-cols-[1fr_90px_90px] items-baseline gap-3 text-[13px]">
                <span className="text-foreground/90 truncate">{r.customerName}</span>
                <span className={`tabular-nums text-[12px] ${tone}`}>{ageLabel}</span>
                <span className="text-right tabular-nums font-medium">{fmtCompact(r.openBalance)}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="border-t border-border/60 mt-3 pt-2 flex items-center justify-between text-[13px]">
        <span className="font-medium">Total open</span>
        <span className="font-semibold tabular-nums">{fmtCompact(total)}</span>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Market Contribution
// ───────────────────────────────────────────────────────────────────────

interface MarketRow { market: string; revenue: number; marginPct: number; contribution: number }

function MarketContribution({ rows }: { rows: MarketRow[] }) {
  const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
  const top = rows.slice(0, 3);
  const more = rows.length - top.length;
  return (
    <div className="bg-background border border-border/60 p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Market Contribution</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{rows.length} market{rows.length === 1 ? "" : "s"} served</p>
        </div>
        {more > 0 && <span className="text-[11px] text-primary">+{more} more →</span>}
      </div>
      <div className="grid grid-cols-[1fr_80px_70px_80px_1fr_36px] gap-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground font-medium pb-1.5 border-b border-border/60">
        <span>Market</span>
        <span className="text-right">Revenue</span>
        <span className="text-right">Margin %</span>
        <span className="text-right">Contribution</span>
        <span>Share</span>
        <span className="text-right">%</span>
      </div>
      {top.map((m) => {
        const share = totalRev > 0 ? (m.revenue / totalRev) * 100 : 0;
        return (
          <div key={m.market} className="grid grid-cols-[1fr_80px_70px_80px_1fr_36px] gap-3 items-center py-1.5 border-b border-border/40 last:border-0 text-[12px]">
            <span className="text-foreground font-medium truncate">{m.market}</span>
            <span className="text-right tabular-nums">{fmtCompact(m.revenue)}</span>
            <span className="text-right tabular-nums text-muted-foreground">{m.marginPct.toFixed(0)}%</span>
            <span className="text-right tabular-nums font-medium text-success">{fmtCompact(m.contribution)}</span>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${share}%` }} />
            </div>
            <span className="text-right tabular-nums text-muted-foreground">{share.toFixed(0)}%</span>
          </div>
        );
      })}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Main composer
// ───────────────────────────────────────────────────────────────────────

export interface FinanceCommandCenterProps {
  jobs: Job[];
  payRunRecords: PayRunRecord[];
  heldRows: PayRunRow[];
  overdueCount: number;
  overdueAmount: number;
  pendingFieldAddsCount: number;
  pendingFieldAddsAmount: number;
}

export function FinanceCommandCenter({
  jobs,
  payRunRecords,
  heldRows,
  overdueCount,
  overdueAmount,
  pendingFieldAddsCount,
  pendingFieldAddsAmount,
}: FinanceCommandCenterProps) {
  const jobsWithFin = jobs.filter((j) => j.financials);
  const totalRevenue = jobsWithFin.reduce((s, j) => s + j.financials!.contractValue, 0);

  // Period toggle — MTD/QTD/YTD drives the analytic windows below.
  const [period, setPeriod] = useState<"MTD" | "QTD" | "YTD">("MTD");
  const windowDays = period === "MTD" ? 30 : period === "QTD" ? 90 : 365;
  const dsoWindow = Math.max(windowDays, 90);

  // ── Real DSO + AR Aging + Net Margin (Phase 1.5 wiring) ─────────────
  const dso = useMemo(() => computeDso(jobsWithFin, dsoWindow), [jobsWithFin, dsoWindow]);
  const ar = useMemo(() => computeArAging(jobsWithFin), [jobsWithFin]);
  const marginDelta = useMemo(() => computeMarginDelta(jobsWithFin, windowDays), [jobsWithFin, windowDays]);
  const marginPct = marginDelta.current;

  // ── Cash Position + Liquidity Forecast (Phase 1.5 wiring) ───────────
  const cash = useMemo(() => computeCashPosition(jobsWithFin, payRunRecords), [jobsWithFin, payRunRecords]);
  const cashPosition = cash.current;
  const forecast = useMemo(
    () => computeLiquidityForecast(jobsWithFin, payRunRecords, cashPosition),
    [jobsWithFin, payRunRecords, cashPosition],
  );

  // Sparkline — derived from forecast actuals so it tells the same story as the chart.
  const sparkline = useMemo(
    () => forecast.points.map((p) => p.actual ?? p.projected ?? 0).filter((v) => v > 0),
    [forecast.points],
  );

  // AR Aging buckets — real.
  const arBuckets: ArBucket[] = ar.buckets;

  // Collected in selected period — drives the swapped Working Capital tile.
  const collected = useMemo(() => computeCollectedInWindow(jobsWithFin, windowDays), [jobsWithFin, windowDays]);

  // Top receivables — replaces the Upcoming AP stub.
  const topReceivables = useMemo(() => computeTopReceivables(jobsWithFin, 5), [jobsWithFin]);

  // ── Decision Queue — derived from real signals.
  const queueItems: QueueItem[] = [];
  // 1. Held pay-runs (critical) — surface up to 2 separately so the queue feels real.
  const heldSlice = heldRows.slice(0, 2);
  heldSlice.forEach((held) => {
    const amt = held.view.baseAmount + held.view.fieldAddPendingTotal;
    queueItems.push({
      id: `held-${held.job.id}`,
      severity: "critical",
      title: `Pay run held · ${fmtCompact(amt)}`,
      context: `${held.job.customerName} · ${held.view.holdReasons.length} hold reason${held.view.holdReasons.length === 1 ? "" : "s"}`,
      tag: "Margin Alert",
      href: "/finance/pay-runs?pivot=held",
      action: "Authorize now",
    });
  });
  if (heldRows.length > 2) {
    queueItems.push({
      id: "held-more",
      severity: "warn",
      title: `${heldRows.length - 2} more pay run${heldRows.length - 2 === 1 ? "" : "s"} held`,
      context: `Total held volume across portfolio`,
      tag: "Margin Alert",
      href: "/finance/pay-runs?pivot=held",
      action: "View all",
    });
  }

  // 2. Pending field-add approvals (warn)
  if (pendingFieldAddsCount > 0) {
    queueItems.push({
      id: "field-adds",
      severity: "warn",
      title: `Field-add approvals · ${fmtCompact(pendingFieldAddsAmount)}`,
      context: `${pendingFieldAddsCount} submission${pendingFieldAddsCount === 1 ? "" : "s"} awaiting review`,
      tag: "Approval Required",
      href: "/finance/pay-runs?pivot=approvals",
      action: "Review now",
    });
  }
  // 3. Overdue receivables (info)
  if (overdueCount > 0) {
    const sampleJob = jobsWithFin.find((j) => j.financials!.paymentStatus === "Overdue");
    queueItems.push({
      id: "overdue",
      severity: "info",
      title: `Past due · ${fmtCompact(overdueAmount)}`,
      context: `${overdueCount} customer${overdueCount === 1 ? "" : "s"}${sampleJob ? ` · incl. ${sampleJob.customerName}` : ""}`,
      tag: "Collection Risk",
      href: "/accounting?filter=overdue",
      action: "Review now",
    });
  }
  // 4. Ready-to-release (info, positive)
  const recentRelease = payRunRecords[payRunRecords.length - 1];
  if (recentRelease) {
    queueItems.push({
      id: "released",
      severity: "info",
      title: `Recent release · ${fmtCompact(recentRelease.netReleased)}`,
      context: `Last pay run released ${new Date(recentRelease.releasedAt).toLocaleDateString()}`,
      tag: "Released",
      href: "/finance/pay-runs?pivot=paid",
      action: "View",
    });
  }

  // ── Market Contribution — group by region (multi-market, single branch).
  const marketMap = new Map<string, { revenue: number; margin: number }>();
  jobsWithFin.forEach((j) => {
    const key = j.region || "Unassigned";
    const m = marketMap.get(key) || { revenue: 0, margin: 0 };
    m.revenue += j.financials!.contractValue;
    m.margin += j.financials!.marginEstimate;
    marketMap.set(key, m);
  });
  const marketRows: MarketRow[] = Array.from(marketMap.entries())
    .map(([market, m]) => ({
      market,
      revenue: m.revenue,
      marginPct: m.revenue > 0 ? (m.margin / m.revenue) * 100 : 0,
      contribution: m.margin,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  return (
    <section className="space-y-3">
      {/* Header */}
      <header className="pb-3 border-b border-border/60">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Finance Command Center</h1>
            <p className="text-[12px] text-muted-foreground mt-1">
              Q{Math.floor(new Date().getMonth() / 3) + 1} {new Date().getFullYear()} · {queueItems.length} active decision{queueItems.length === 1 ? "" : "s"}
              {heldRows.length > 0 ? ` · ${heldRows.length} held` : ""}
              {overdueCount > 0 ? ` · ${overdueCount} overdue` : ""}
            </p>
          </div>
          <div className="flex items-center border border-border/60 text-[12px]">
            {(["MTD", "QTD", "YTD"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={
                  period === p
                    ? "px-3 py-1 bg-primary/15 text-primary font-medium ring-1 ring-primary/30"
                    : "px-3 py-1 text-muted-foreground hover:text-foreground"
                }
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </header>

      <FinanceNavStrip />



      {/* KPI Row — single flat frame, divided */}
      <div className="grid grid-cols-2 md:grid-cols-5 border border-border/60 divide-x divide-y md:divide-y-0 divide-border/60 bg-background">

        <KpiCard
          hero
          label="Cash Position"
          value={fmtCompact(cashPosition)}
          delta={
            cash.deltaPct != null
              ? {
                  value: `${cash.deltaPct >= 0 ? "+" : ""}${cash.deltaPct.toFixed(1)}%`,
                  direction: cash.deltaPct >= 0 ? "up" : "down",
                  tone: cash.deltaPct >= 0 ? "good" : "bad",
                }
              : undefined
          }
          hint={cash.deltaPct != null ? "vs prior 30d" : undefined}
          sparkline={sparkline}
          info={`Operating cash on hand. Net of $${Math.round(cash.inflows30d / 1000)}K inflows and $${Math.round(cash.outflows30d / 1000)}K pay-run outflows over the last 30 days, layered on a $2.5M opening float.`}
        />

        <KpiCard
          label="DSO"
          value={dso.days != null ? String(dso.days) : "—"}
          unit={dso.days != null ? "days" : undefined}
          delta={
            dso.deltaDays != null
              ? {
                  value: `${dso.deltaDays >= 0 ? "+" : ""}${dso.deltaDays} day${Math.abs(dso.deltaDays) === 1 ? "" : "s"}`,
                  direction: dso.deltaDays <= 0 ? "down" : "up",
                  tone: dso.deltaDays <= 0 ? "good" : "bad",
                }
              : undefined
          }
          hint={dso.deltaDays != null ? `vs prior ${dsoWindow}d` : undefined}
          info={`Days Sales Outstanding — amount-weighted average days from invoice to payment over the last ${dsoWindow} days. ${dso.sample} invoice${dso.sample === 1 ? "" : "s"} in sample.`}
        />
        <KpiCard
          label={`Collected ${period}`}
          value={fmtCompact(collected.amount)}
          delta={
            collected.deltaPct != null
              ? {
                  value: `${collected.deltaPct >= 0 ? "+" : ""}${collected.deltaPct.toFixed(1)}%`,
                  direction: collected.deltaPct >= 0 ? "up" : "down",
                  tone: collected.deltaPct >= 0 ? "good" : "bad",
                }
              : undefined
          }
          hint={collected.deltaPct != null ? "vs prior period" : `${collected.count} payment${collected.count === 1 ? "" : "s"}`}
          info={`Total cash collected this ${period} (sum of invoices marked paid in the period). ${collected.count} payment${collected.count === 1 ? "" : "s"}.`}
        />
        <KpiCard
          label={`Net Margin ${period}`}
          value={`${marginPct.toFixed(1)}%`}
          delta={
            marginDelta.deltaPp != null
              ? {
                  value: `${marginDelta.deltaPp >= 0 ? "+" : ""}${marginDelta.deltaPp.toFixed(1)}pp`,
                  direction: marginDelta.deltaPp >= 0 ? "up" : "down",
                  tone: marginDelta.deltaPp >= 0 ? "good" : "bad",
                }
              : undefined
          }
          hint={marginDelta.deltaPp != null ? "vs prior period" : "lifetime"}
          info={`Net margin % across jobs sold in the current ${period} window (margin ÷ contract value). Falls back to lifetime margin if no sales in window.`}
        />
      </div>



      {/* Forecast + Decision Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-2">
        <LiquidityForecast points={forecast.points} projectedMin={forecast.projectedMin} />
        <div className="bg-background border border-border/60 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Decision Queue</p>
            <span className="text-[11px] text-muted-foreground tabular-nums">{queueItems.length} active</span>
          </div>
          {queueItems.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <CheckCircle2 className="h-6 w-6 text-success mb-2" />
              <p className="text-sm text-foreground">No decisions pending</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">You're caught up.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {queueItems.slice(0, 3).map((item) => <QueueRow key={item.id} item={item} />)}
            </div>
          )}
          {queueItems.length > 3 && (
            <div className="text-center px-4 py-2 mt-auto border-t border-border/60">
              <Link to="/finance/pay-runs" className="text-[12px] text-primary hover:underline">
                View all decisions ({queueItems.length})
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* AR Aging + Top Receivables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <ArAging buckets={arBuckets} />
        <TopReceivables rows={topReceivables} />
      </div>

      {/* Market Contribution */}
      {marketRows.length > 0 && <MarketContribution rows={marketRows} />}
    </section>
  );
}
